import React, { useState, type FC } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './translations.json';

type SupportedLanguage = keyof typeof translations;

interface VerifyEmailFormProps {
    title: string;
    subtitle: string;
    offerText: string;
    email: string;
    onSubmit: (code: string) => Promise<void>;
    onResend: () => Promise<void>;
    onBack: () => void;
}

const VerifyEmailForm: FC<VerifyEmailFormProps> = ({ title, subtitle, offerText, email, onSubmit, onResend, onBack }) => {
    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (!code.trim()) {
                setError(content.empty);
                setLoading(false);
                return;
            }

            await onSubmit(code);
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || content.generic;
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        setError(null);

        try {
            await onResend();
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || content.resendGeneric;
            setError(message);
        } finally {
            setResending(false);
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Button
                startIcon={<ArrowBack />}
                onClick={onBack}
                sx={{ mb: 2, color: '#90caf9', textTransform: 'none' }}
            >
                {content.back}
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
                {title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                {subtitle}
            </Typography>

            <Typography variant="body2" sx={{ color: '#ffffff', mb: 0.5 }}>
                {offerText}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                {email}
            </Typography>

            {error && (
                <Typography variant="body2" sx={{ color: '#ef4444', mb: 2, fontWeight: 500 }}>
                    {error}
                </Typography>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {content.code}
                </Typography>
                <TextField
                    fullWidth
                    id="code"
                    placeholder="123456"
                    name="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    disabled={loading || resending}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '8px',
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                            '&:hover fieldset': { borderColor: '#1976d2' },
                            '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                        },
                    }}
                    inputProps={{
                        maxLength: 6,
                        style: {
                            textAlign: 'center',
                            fontSize: '1.25rem',
                            letterSpacing: '0.4rem',
                            color: '#ffffff',
                        },
                    }}
                />

                <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={loading || resending || !code.trim()}
                    sx={{
                        py: 1.2,
                        borderRadius: '8px',
                        backgroundColor: '#1976d2',
                        color: '#ffffff',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#115293' },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            color: 'rgba(255, 255, 255, 0.3)',
                        }
                    }}
                >
                    {loading ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1, color: '#ffffff' }} />
                            {content.verifying}
                        </>
                    ) : (
                        content.verify
                    )}
                </Button>
                <Button
                    fullWidth
                    type="button"
                    onClick={handleResend}
                    disabled={loading || resending}
                    sx={{ mt: 1, color: '#90caf9', textTransform: 'none' }}
                >
                    {resending ? (
                        <>
                            <CircularProgress size={18} sx={{ mr: 1, color: '#90caf9' }} />
                            {content.resending}
                        </>
                    ) : (
                        content.resend
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default VerifyEmailForm;
